/* global google */
import SnazzyInfoWindow from 'snazzy-info-window';
import mapStyles from '@/scripts/blocks/google-maps-styles.json';
import getElementsArray from '@/scripts/utils/get-elements-array';

/**
 * Google Maps utility
 *
 * Automatically inserts a Google API script for you and provides its configuration
 * from data attributes of each
 * DOM element where the map should appear
 *
 *
 * @param elements              - selector, Element or NodeList of elements where map
 * should be placed. Each element can have his own (data-lat, data-marker-lat) attributes
 * @param apiKey                - [optional] Google Maps Javascript API key
 * @param infoWindowConfig      - [optional] config provided by info window config
 *
 * Usage:
 *
 * 1. API key in the data-attribute. You should specify data-api-key for at least
 * one of your maps elements.
 * HTML:
 * <div class='map' data-api-key='AIzaSyARG6MDaRo-9lIHHGq8jHjVgyPcF6QC5FM'
 * data-lat='47.085' data-lng='6.48' data-zoom='17' [data-marker-lat='' data-marker-lng='']
 * data-marker-icon=''></div>
 * JS:
 * @import googleMaps from './widgets/google-maps';
 * googleMaps('.map');
 *
 * 2. API key in the script
 * HTML:
 * <div class='map' data-lat='47.085' data-lng='6.48' data-zoom='17'
 * [data-marker-lat='' data-marker-lng=''] data-marker-icon=''></div>
 * JS:
 * @import googleMaps from './widgets/google-maps';
 * googleMaps('.map', { apiKey: 'AIzaSyARG6MDaRo-9lIHHGq8jHjVgyPcF6QC5FM' });
 *
 */
export default function (elements, { apiKey, infoWindowConfig }) {
  const elementsArray = getElementsArray(elements);

  if (!elementsArray.length) {
    return;
  }

  if (!apiKey) {
    throw new Error('You should specify data-api-key for at least one of your maps elements');
  }

  // add script
  const script = document.createElement('script');
  script.defer = true;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap`;
  document.body.appendChild(script);

  // process every map element, with its markers, infowindows etc.
  window.initMap = () => {
    Array.prototype.forEach.call(elementsArray, (el) => {
      const {
        lat, lng, zoom, markerLat, markerLng, markerIcon, popupId,
      } = $(el).data();
      const map = new google.maps.Map(el, {
        zoom,
        center: {
          lat,
          lng,
        },
        scrollwheel: false,
      });
      const marker = new google.maps.Marker({
        position: { lat: markerLat, lng: markerLng },
        map,
        icon: markerIcon,
      });
      const styledMapType = new google.maps.StyledMapType(mapStyles);

      map.mapTypes.set('styled_map', styledMapType);
      map.setMapTypeId('styled_map');

      if (popupId) {
        const popup = document.getElementById(popupId);
        if (!popup) {
          throw new Error(`Cannot find element with id="${popup}"`);
        }
        SnazzyInfoWindow({ marker, content: popup.innerHTML, ...infoWindowConfig });
      }
    });
  };
}



// WEBPACK FOOTER //
// ./scripts/blocks/google-map.js